import {
    ChartBarIcon,
    PhoneIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import AgentPhone from '../components/AgentPhone';
import { useAuth } from '../contexts/AuthContext';
import { assignmentsAPI } from '../services/assignments';
import { contactsAPI } from '../services/contacts';

const AgentLeads = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dateFilter, setDateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('assigned'); // 'assigned' or 'self-added'
  const [callingContact, setCallingContact] = useState(null);

  // Fetch assigned leads
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['my-leads'],
    queryFn: () => assignmentsAPI.getMyLeads({ status: 'pending' }),
    refetchInterval: 30000,
  });

  // Fetch self-added contacts (contacts without assignments)
  const { data: selfAddedData, isLoading: selfAddedLoading } = useQuery({
    queryKey: ['self-added-contacts'],
    queryFn: () => contactsAPI.getContacts({
      assigned: 'unassigned',
      limit: 100
    }),
    refetchInterval: 30000,
  });

  // Process leads based on filter
  const allLeads = useMemo(() => {
    if (statusFilter === 'assigned') {
      const leads = leadsData?.leads || [];
      // Filter by date if needed
      if (dateFilter === 'today') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return leads.filter(lead => {
          const assignedDate = new Date(lead.assignedAt);
          assignedDate.setHours(0, 0, 0, 0);
          return assignedDate.getTime() === today.getTime();
        });
      }
      return leads;
    } else {
      // Self-added contacts
      const contacts = selfAddedData?.contacts || [];
      // Filter by date if needed
      if (dateFilter === 'today') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return contacts.filter(contact => {
          const createdDate = new Date(contact.createdAt);
          createdDate.setHours(0, 0, 0, 0);
          return createdDate.getTime() === today.getTime();
        });
      }
      return contacts;
    }
  }, [leadsData, selfAddedData, statusFilter, dateFilter]);

  // Transform contacts to lead format for display
  const leads = useMemo(() => {
    if (statusFilter === 'assigned') {
      return allLeads;
    } else {
      // Transform contacts to match lead structure
      return allLeads.map(contact => ({
        assignmentId: `self-${contact.id}`,
        status: 'self-added',
        assignedAt: contact.createdAt,
        contact: {
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          phone: contact.phone,
          email: contact.email,
          company: contact.company,
          title: contact.title
        },
        campaign: {
          name: contact.campaignId || '-'
        }
      }));
    }
  }, [allLeads, statusFilter]);

  const handleStartCall = (lead) => {
    // Check if already calling
    if (callingContact) {
      return;
    }

    // Open manual call interface
    setCallingContact({
      id: lead.contact.id,
      firstName: lead.contact.firstName,
      lastName: lead.contact.lastName,
      phone: lead.contact.phone,
      company: lead.contact.company,
      title: lead.contact.title,
      organizationId: user?.organizationId
    });
  };

  const handleCallComplete = () => {
    setCallingContact(null);
    queryClient.invalidateQueries({ queryKey: ['my-leads'] });
    queryClient.invalidateQueries({ queryKey: ['self-added-contacts'] });
  };

  if (leadsLoading || (statusFilter === 'self-added' && selfAddedLoading)) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Leads</h1>
          <p className="text-gray-600">Manage and track your assigned leads</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Date:</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Type:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="assigned">Assigned</option>
              <option value="self-added">Self-Added</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Assigned</dt>
                  <dd className="text-lg font-medium text-gray-900">{leads.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending Calls</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {leads.filter(l => l.status === 'pending').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {leads.filter(l => l.status === 'in_progress').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Leads Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Assigned Leads</h3>
          {leads.length === 0 ? (
            <div className="text-center py-8">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leads assigned</h3>
              <p className="mt-1 text-sm text-gray-500">
                Contact your manager to get leads assigned to you.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <tr key={lead.assignmentId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {lead.contact.firstName?.charAt(0) || '?'}
                                {lead.contact.lastName?.charAt(0) || '?'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.contact.firstName} {lead.contact.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{lead.contact.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lead.contact.company || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lead.campaign?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          lead.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : lead.status === 'self-added'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {lead.status === 'self-added' ? 'Self-Added' : lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleStartCall(lead)}
                          disabled={!!callingContact}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          <PhoneIcon className="h-4 w-4 mr-1" />
                          {callingContact ? 'Calling...' : 'Call'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Direct Call Interface Modal */}
      {callingContact && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <AgentPhone
            contact={callingContact}
            onClose={() => {
              setCallingContact(null);
              queryClient.invalidateQueries({ queryKey: ['my-leads'] });
              queryClient.invalidateQueries({ queryKey: ['self-added-contacts'] });
            }}
            onCallComplete={handleCallComplete}
          />
        </div>
      )}
    </div>
  );
};

export default AgentLeads;
